import { LinkTo } from '@ember/routing';
import { htmlSafe } from '@ember/template';
import { trackedObject } from '@ember/reactive/collections';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import SyfFaqs from './faqs';
import TioStepOverview from 'tio-common/components/tio/step-overview';
import type { MatchPlanExtension as MatchPlanModel } from 'tio-data/data/resources/match-plan.ext';
import Button from 'tio-ui/components/button';
import { Section, VStack } from 'tio-ui/components/layout';
import PageHero from 'tio-ui/components/page-hero';
import { PhPiggyBank } from 'ember-phosphor-icons';

export interface SyfEnrollmentSignature {
  Args: {
    onBeginEnrollment: () => void;
    beginEnrollmentLoading: boolean;
    matchPlan: MatchPlanModel;
  };
}

class SyfEnrollmentComponent extends Component<SyfEnrollmentSignature> {
  options: Record<string, boolean> = trackedObject({
    option1: false,
    option2: false,
    option3: false,
    option4: false,
  });

  option1Text = `<div><span class="tio-fg-brand">I do not contribute</span> to my employer's 401(k)</div>`;
  option2Text = `<div>I make <span class="tio-fg-brand">contributions</span> to my 401(k) and do not receive the full match from my employer</div>`;
  option3Text = `<div>I make contributions to my 401(k), but <span class="tio-fg-brand">I am unsure whether I receive the full match from my employer</span></div>`;
  option4Text = `<div><span class="tio-fg-brand">I contribute to my 401(k)</span> and receive the full possible match from my employer</div>`;

  get secureOptionCards() {
    return [
      { text: this.option1Text, dataKey: 'option1' },
      { text: this.option2Text, dataKey: 'option2' },
      { text: this.option3Text, dataKey: 'option3' },
      { text: this.option4Text, dataKey: 'option4' },
    ].map((card) => ({ ...card, selected: this.options[card.dataKey] ?? false }));
  }

  get enrollmentSteps() {
    return ['Connect your loans', 'Confirm your loan payments', 'Receive matching contributions'];
  }

  get selectedOptionText() {
    if (this.options['option1']) return this.option1Text;
    if (this.options['option2']) return this.option2Text;
    if (this.options['option3']) return this.option3Text;
    if (this.options['option4']) return this.option4Text;
    return '';
  }

  showOptionNumber = (option: string) => {
    for (const key in this.options) {
      if (key !== option) {
        this.options[key] = false;
      }
    }
    this.options[option] = true;

    // The detail panel mounts on the upcoming render flush; a synchronous
    // lookup misses it on the first selection, so scroll after the frame.
    requestAnimationFrame(() => {
      document.getElementById('option_detail')?.scrollIntoView({ behavior: 'smooth' });
    });
  };

  <template>
    <VStack class="gap-8">
      <PageHero
        @icon={{PhPiggyBank}}
        @title={{t "syf.enrollment.heading"}}
        @subtitle={{t "syf.enrollment.subheading"}}
        @gradient="brand-soft"
        class="tio-elevation-card-raised"
      />

      <Section>
        <:header>{{t "syf.enrollment.what_is_secure_your_future"}}</:header>
        <:body>
          <VStack>
            <p>
              {{t "syf.enrollment.secure_2_intro"}}
              <span class="font-semibold">{{t "syf.enrollment.start_date"}}</span>
            </p>
            <p>
              <span class="tio-fg-brand font-semibold">{{t "syf.enrollment.program_name"}}</span>
              {{t "syf.enrollment.benefit_description"}}
              <span class="font-semibold">{{t "syf.enrollment.qualifying_payments"}}</span>
              {{t "syf.enrollment.to_get_an"}}
              <span class="font-semibold">{{t "syf.enrollment.employer_match"}}</span>
            </p>
          </VStack>
        </:body>
      </Section>

      <Section>
        <:header>{{t "syf.enrollment.ready_to_enroll"}}</:header>
        <:body>
          <VStack>
            <p>
              {{t "syf.enrollment.enrollment_process"}}
            </p>
            <div class="tio-flex-center">
              <TioStepOverview @steps={{this.enrollmentSteps}} />
            </div>

            <Button
              @intent="primary"
              {{on "click" @onBeginEnrollment}}
              @isRunning={{@beginEnrollmentLoading}}
              class="w-60 mx-auto"
            >
              {{t "syf.enrollment.start"}}
            </Button>
          </VStack>
        </:body>
      </Section>

      <Section>
        <:header>{{t "syf.enrollment.want_to_learn_more"}}</:header>
        <:body>
          <VStack>
            <p class="leading-relaxed">
              {{t "syf.enrollment.learn_more_intro"}}
            </p>
            <p>{{t "syf.enrollment.choose_option"}}
              <span class="font-semibold">{{t "syf.enrollment.choose_option_bold"}}</span></p>

            <div class="app-card-grid app-card-grid--medium font-semibold">
              {{#each this.secureOptionCards as |card|}}
                <button
                  type="button"
                  data-test-syf-option-card
                  aria-pressed={{if card.selected "true" "false"}}
                  class="tio-option-card {{if card.selected 'tio-option-card--selected'}}"
                  {{on "click" (fn this.showOptionNumber card.dataKey)}}
                >
                  <div class="text-center">
                    {{htmlSafe card.text}}
                  </div>
                </button>
              {{/each}}
            </div>
          </VStack>
        </:body>
      </Section>

      {{#if this.selectedOptionText}}
        <div id="option_detail">
          <Section>
            <:header>{{t "syf.enrollment.how_can_this_help"}}</:header>
            <:body>
              <VStack>
                <div class="font-semibold">{{htmlSafe this.selectedOptionText}}</div>
                {{#if this.options.option1}}
                  <h3 class="tio-text-base-medium">{{t "syf.enrollment.option1.intro"}}</h3>
                  <ol class="list-decimal p-4 leading-loose">
                    <li>{{t "syf.enrollment.option1.point1"}}</li>
                    <li>{{t "syf.enrollment.option1.point2"}}</li>
                  </ol>
                {{/if}}
                {{#if this.options.option2}}
                  <h3 class="tio-text-base-medium">{{t "syf.enrollment.option2.intro"}}</h3>
                  <ol class="list-decimal p-4 leading-loose">
                    <li>{{t "syf.enrollment.option2.point1"}}</li>
                    <li>{{t "syf.enrollment.option2.point2"}}</li>
                  </ol>
                {{/if}}
                {{#if this.options.option3}}
                  <h3 class="tio-text-base-medium">{{t "syf.enrollment.option3.intro"}}</h3>
                  <ol class="list-decimal p-4 leading-loose">
                    <li>{{t "syf.enrollment.option3.point1"}}</li>
                    <li>{{t "syf.enrollment.option3.point2"}}</li>
                    <li>{{t "syf.enrollment.option3.point3"}}</li>
                  </ol>
                {{/if}}
                {{#if this.options.option4}}
                  <h3 class="tio-text-base-medium">{{t "syf.enrollment.option4.intro"}}</h3>
                  <ol class="list-decimal p-4 leading-loose">
                    <li>{{t "syf.enrollment.option4.point1"}}</li>
                    <li>{{t "syf.enrollment.option4.point2"}}</li>
                    <li>{{t "syf.enrollment.option4.point3"}}
                      <LinkTo
                        @route="authenticated.repaying-student-debt.coaching.schedule"
                        class="app-text-link app-text-link--inline"
                      >{{t "syf.enrollment.option4.here"}}</LinkTo>
                      {{t "syf.enrollment.option4.to_contact_coach"}}</li>
                  </ol>
                {{/if}}
              </VStack>
            </:body>
          </Section>
        </div>
      {{/if}}

      <SyfFaqs @faqs={{@matchPlan.faqs}} />
    </VStack>
  </template>
}

export default SyfEnrollmentComponent;
